import type { Rect } from 'hamo'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { useTempus } from 'tempus/react'
import { colors } from '~/styles/colors'

export interface DotsGridCanvasRef {
  active: () => void
  inactive: () => void
  enter: () => void
  exit: () => void
}

interface DotsGridCanvasProps {
  rect: Rect
  className?: string
  accentColor?: string
  baseColor?: string
  speed?: number
  ref: (instance: DotsGridCanvasRef | null) => void
  disabled?: boolean
}

export function DotsGridCanvas({
  rect,
  className,
  accentColor = colors.blue,
  baseColor = '#414141',
  speed = 1,
  ref,
  disabled = false,
}: DotsGridCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null!)
  const blockHoverRef = useRef(false)
  const timeRef = useRef(0)
  const baseColorRef = useRef(baseColor)
  const accentColorRef = useRef(accentColor)
  const animateRef = useRef(false)
  const animateDirectionRef = useRef<1 | -1>(1)
  const glitchStateRef = useRef<boolean[][]>([])
  const dotVulnerabilityRef = useRef<number[][]>([])

  const gridSpecs = useMemo(() => {
    if (!rect.width || !rect.height)
      return {
        dotSpacing: 1,
        cols: 0,
        rows: 0,
        marginX: 0,
        marginY: 0,
        dotSize: 1,
      }

    const { width, height } = rect
    /* PARAMETERS */
    // manually adjust to prevent aliasing
    const desiredRows = 14
    const alpha = 1

    const calculatedDotSize = height / (desiredRows * (1 + alpha) + 1)
    const calculatedDotSpacing = alpha * calculatedDotSize

    const dotSize = calculatedDotSize
    const dotSpacing = calculatedDotSpacing
    const dotArea = dotSpacing + dotSize

    const exactCols = (width - dotSize) / dotArea
    const cols = Math.floor(exactCols)
    const rows = desiredRows

    // totalGridWidth is the width from the start of the first dot to the end of the last dot
    const totalGridWidth =
      cols * dotSize + (cols > 0 ? (cols - 1) * dotSpacing : 0)
    const marginX = (width - totalGridWidth) / 2

    // totalGridHeight is the height from the start of the first dot to the end of the last dot
    const totalGridHeight =
      rows * dotSize + (rows > 0 ? (rows - 1) * dotSpacing : 0)
    const marginY = (height - totalGridHeight) / 2

    return {
      dotSpacing,
      cols,
      rows,
      marginX,
      marginY,
      dotSize,
    }
  }, [rect])

  useEffect(() => {
    // Init random glitch state and vulnerability for each dot
    if (gridSpecs.rows > 0 && gridSpecs.cols > 0) {
      const newGlitchState: boolean[][] = []
      const newVulnerabilities: number[][] = []

      for (let i = 0; i < gridSpecs.rows; i++) {
        const rowGlitchState: boolean[] = []
        const rowVulnerabilities: number[] = []
        for (let j = 0; j < gridSpecs.cols; j++) {
          rowGlitchState.push(false)
          // Assign a random vulnerability (0 to <1)
          rowVulnerabilities.push(Math.random())
        }
        newGlitchState.push(rowGlitchState)
        newVulnerabilities.push(rowVulnerabilities)
      }
      glitchStateRef.current = newGlitchState
      dotVulnerabilityRef.current = newVulnerabilities
    }
  }, [gridSpecs.rows, gridSpecs.cols])

  const drawGrid = useCallback(
    (progress: number) => {
      const { dotSize, marginX, marginY, cols, rows, dotSpacing } = gridSpecs
      const { width, height } = rect
      const ctx = canvasRef.current?.getContext('2d')

      if (!ctx || !width || !height || cols === 0 || rows === 0) {
        return
      }

      ctx.clearRect(0, 0, width, height)

      for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
          if (
            !dotVulnerabilityRef.current[i] ||
            glitchStateRef.current[i] === undefined
          )
            continue

          let isGlitched = glitchStateRef.current[i][j]
          const vulnerability = dotVulnerabilityRef.current[i][j]

          if (animateDirectionRef.current === 1) {
            if (!isGlitched && progress >= vulnerability) {
              glitchStateRef.current[i][j] = true
              isGlitched = true
            }
          } else {
            if (isGlitched && progress < vulnerability) {
              glitchStateRef.current[i][j] = false
              isGlitched = false
            }
          }

          if (disabled) {
            isGlitched = true
          }

          const colorToUse = isGlitched
            ? accentColorRef.current
            : baseColorRef.current
          const dotTopLeftX = marginX + j * (dotSize + dotSpacing)
          const dotTopLeftY = marginY + i * (dotSize + dotSpacing)

          ctx.fillStyle = colorToUse
          ctx.fillRect(dotTopLeftX, dotTopLeftY, dotSize, dotSize)
        }
      }
    },
    [gridSpecs, rect, accentColor, baseColor, disabled]
  )

  useEffect(() => {
    if (!rect.width || !rect.height) return

    const { width, height } = rect
    const ctx = canvasRef.current.getContext('2d')

    if (!ctx) return
    ctx.imageSmoothingEnabled = false
    ctx.clearRect(0, 0, width, height)
    const dpr = window.devicePixelRatio

    canvasRef.current.width = Math.round(width * dpr)
    canvasRef.current.height = Math.round(height * dpr)
    ctx.scale(dpr, dpr)

    drawGrid(-1)
  }, [rect])

  useTempus((_, deltaTime) => {
    if (!animateRef.current) {
      timeRef.current = animateDirectionRef.current === 1 ? 1 : 0
      drawGrid(-1)
      return
    }

    if (timeRef.current > 1) {
      timeRef.current = 1
      animateRef.current = false
      return
    }

    if (timeRef.current < 0) {
      timeRef.current = 0
      animateRef.current = false
    }

    timeRef.current += deltaTime * 0.0016 * speed * animateDirectionRef.current
    drawGrid(timeRef.current)
  })

  useImperativeHandle(ref, () => ({
    active: () => {
      blockHoverRef.current = true
    },
    inactive: () => {
      blockHoverRef.current = false
    },
    enter: () => {
      if (blockHoverRef.current) return

      timeRef.current = 0
      animateRef.current = true
      animateDirectionRef.current = 1
    },
    exit: () => {
      if (blockHoverRef.current) return

      timeRef.current = 1
      animateRef.current = true
      animateDirectionRef.current = -1
    },
  }))

  return <canvas ref={canvasRef} className={className} />
}
